import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { ChevronRightIcon } from '../../../components/icons';
import type { DocTreeEntity, DocTreeNode } from './types';

/*
 * Matches the reader's own doc-nav rail (readerStyles.ts's `.md-nav__link`
 * family) so the tree reads as the same control, just fed from the whole
 * catalog instead of one doc set's page list: a 32px row rhythm, a dim label
 * for an ordinary page, a bold one for a section, and the active page taking
 * the portal's selected-row fill plus a 3px brand-yellow rail.
 */
const useStyles = makeStyles({
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  toggle: {
    flex: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 20,
    height: 20,
    padding: 0,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    // Matches mkdocs' own nav icon, which takes the section label's own
    // (bright) colour rather than a dimmer tertiary tone.
    color: 'var(--bui-fg-primary)',
  },
  chevron: {
    // 20x20, filling the toggle box with no padding, matching mkdocs' own
    // `.md-nav__icon`.
    width: 20,
    height: 20,
    transition: 'transform 0.1s ease',
  },
  chevronOpen: {
    transform: 'rotate(90deg)',
  },
  link: {
    position: 'relative',
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 6,
    minHeight: 32,
    padding: '5px 8px 5px 10px',
    borderRadius: 7,
    fontSize: 12.5,
    lineHeight: 1.3,
    color: 'var(--hone-portal-fg-tertiary)',
    textDecoration: 'none',
    '&:hover': {
      background: 'var(--hone-portal-fill-row-hover)',
      color: 'var(--bui-fg-primary)',
      textDecoration: 'none',
    },
  },
  label: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  section: {
    // A folder's own label, whether or not it also links to docs: reads as a
    // heading over its subtree, matching `.md-nav__item--nested`.
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--bui-fg-primary)',
  },
  muted: {
    cursor: 'default',
    '&:hover': {
      background: 'none',
      color: 'var(--hone-portal-fg-tertiary)',
    },
  },
  active: {
    background: 'var(--hone-portal-fill-selected)',
    color: 'var(--bui-fg-primary)',
    fontWeight: 600,
    '&:hover': {
      background: 'var(--hone-portal-fill-selected)',
      color: 'var(--bui-fg-primary)',
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 6,
      bottom: 6,
      width: 3,
      borderRadius: 2,
      background: 'var(--helix-colors-brand-500)',
    },
  },
  /** The small uppercase tag: a Domain/System's own kind, or a Component's type. */
  meta: {
    flex: 'none',
    fontSize: 10,
    fontWeight: 500,
    letterSpacing: '0.02em',
    textTransform: 'uppercase',
    color: 'var(--hone-portal-fg-tertiary)',
  },
});

/** Indent step per level, matching the reader's own nested-page rhythm. */
const INDENT = 14;
const BASE_INDENT = 12;

/*
 * A leaf is only ever in the tree because `buildDocTree` found it documented,
 * and it's always nested under a kind folder (Components, Repositories, ...),
 * so its own kind would be redundant here — a Component's `spec.type`
 * (service/library/website/...) is shown instead, when set.
 */
function EntityRow({
  entity,
  depth,
  activeRef,
}: {
  entity: DocTreeEntity;
  depth: number;
  activeRef?: string;
}) {
  const classes = useStyles();
  const isActive = activeRef === entity.entityRef;

  return (
    <div className={classes.row}>
      <RouterLink
        to={entity.path}
        className={`${classes.link} ${isActive ? classes.active : ''}`}
        style={{ paddingLeft: BASE_INDENT + depth * INDENT }}
      >
        <span className={classes.label}>{entity.title}</span>
        {entity.type && <span className={classes.meta}>{entity.type}</span>}
      </RouterLink>
    </div>
  );
}

/** A new object each click, so the same direction can be re-applied over manual toggles in between. */
export type ExpandAllSignal = { expand: boolean };

export type DocTreeNodeRowProps = {
  node: DocTreeNode;
  depth: number;
  /** Forces this node (and its descendants) open, e.g. while a search term is active. */
  autoExpand: boolean;
  /** Forces just this specific node open, e.g. the path down to the active doc. */
  forceExpandKeys?: Set<string>;
  /** The entity ref of the doc currently open, if any, for the active-row treatment. */
  activeRef?: string;
  /** Set (to a new object) on every "Expand all"/"Collapse all" click. */
  expandAllSignal?: ExpandAllSignal;
};

/** One Domain or System row: an expand/collapse toggle over its subtree. */
export function DocTreeNodeRow({
  node,
  depth,
  autoExpand,
  forceExpandKeys,
  activeRef,
  expandAllSignal,
}: DocTreeNodeRowProps) {
  const classes = useStyles();
  const forceOpen = autoExpand || !!forceExpandKeys?.has(node.key);
  const [expanded, setExpanded] = useState(
    forceOpen || (expandAllSignal?.expand ?? false),
  );
  const hasContent = node.children.length > 0 || node.entities.length > 0;
  const isActive = node.entity && activeRef === node.entity.entityRef;

  useEffect(() => {
    if (forceOpen) {
      setExpanded(true);
    }
  }, [forceOpen]);

  // A freshly-mounted descendant (e.g. one revealed by this same click) picks
  // this up via its own initial state above; this effect is what moves an
  // already-mounted node when "Expand all"/"Collapse all" is clicked.
  useEffect(() => {
    if (expandAllSignal) {
      setExpanded(expandAllSignal.expand);
    }
  }, [expandAllSignal]);

  return (
    <div>
      <div className={classes.row}>
        {node.entity?.hasDocs ? (
          <RouterLink
            to={node.entity.path}
            className={`${classes.link} ${classes.section} ${
              isActive ? classes.active : ''
            }`}
            style={{ paddingLeft: BASE_INDENT + depth * INDENT }}
          >
            <span className={classes.label}>{node.title}</span>
            <span className={classes.meta}>{node.entity.kind}</span>
          </RouterLink>
        ) : (
          <span
            className={`${classes.link} ${classes.section} ${classes.muted}`}
            style={{ paddingLeft: BASE_INDENT + depth * INDENT }}
          >
            <span className={classes.label}>{node.title}</span>
            {node.entity && (
              <span className={classes.meta}>{node.entity.kind}</span>
            )}
          </span>
        )}
        {hasContent && (
          <button
            type="button"
            className={classes.toggle}
            aria-expanded={expanded}
            onClick={() => setExpanded(value => !value)}
          >
            <ChevronRightIcon
              className={`${classes.chevron} ${
                expanded ? classes.chevronOpen : ''
              }`}
            />
          </button>
        )}
      </div>

      {expanded && hasContent && (
        <div>
          {node.children.map(child => (
            <DocTreeNodeRow
              key={child.key}
              node={child}
              depth={depth + 1}
              autoExpand={autoExpand}
              forceExpandKeys={forceExpandKeys}
              activeRef={activeRef}
              expandAllSignal={expandAllSignal}
            />
          ))}
          {node.entities.map(entity => (
            <EntityRow
              key={entity.entityRef}
              entity={entity}
              depth={depth + 1}
              activeRef={activeRef}
            />
          ))}
        </div>
      )}
    </div>
  );
}
